import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  Grid,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import Autocomplete from '@material-ui/lab/Autocomplete'
import InviteUserModal from '../Employees/InviteUserModal'
import AddCrewModal from '../Crews/AddCrewModal'

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%'
  },
  summaryRow: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    marginBottom: theme.spacing(2)
  },
  helperCard: {
    border: '1px solid #d4dfd7',
    borderRadius: 14,
    background: '#f7fbf8',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  loadingWrap: {
    minHeight: 220,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  actionRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2)
  },
  quickActionGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2)
  },
  muted: {
    color: '#5d6c64'
  },
  tableWrap: {
    border: '1px solid #e2e8e4',
    borderRadius: 12,
    marginTop: theme.spacing(2)
  },
  insightCard: {
    border: '1px solid #e2e8e4',
    borderRadius: 14,
    padding: theme.spacing(2),
    height: '100%'
  },
  listItem: {
    paddingLeft: 0,
    paddingRight: 0,
    borderBottom: '1px solid #edf1ee'
  },
  emptyState: {
    border: '1px dashed #c5d1c8',
    borderRadius: 14,
    padding: theme.spacing(2),
    textAlign: 'center',
    background: '#fbfcfb'
  },
  statusChip: {
    fontWeight: 600
  },
  contactPickerWrap: {
    border: '1px solid #dfe7e2',
    borderRadius: 12,
    padding: theme.spacing(2),
    background: '#fbfcfb',
    marginBottom: theme.spacing(2)
  },
  reviewRow: {
    border: '1px solid #e2e8e4',
    borderRadius: 12,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1.5)
  }
}))

const defaultFormState = {
  firstname: '',
  lastname: '',
  email: '',
  telephone: '',
  subcontractorRole: 'applicator'
}

const buildEmptyCrewReview = () => ({
  crew: null,
  transition: null,
  members: []
})

const getEmployeeAccessStatus = (employee) => {
  if (employee?.subcontractorAccessStatus) return employee.subcontractorAccessStatus
  return employee?.activeEmployee === false ? 'offboarded' : 'active'
}

const getEmployeeDisplayName = (employee) => (
  employee?.displayName ||
  [employee?.firstName, employee?.lastName].filter(Boolean).join(' ') ||
  'Unnamed Employee'
)

const statusChipStyle = (status) => {
  switch (status) {
    case 'active':
      return { backgroundColor: '#e5f4ea', color: '#1b5e20' }
    case 'invited':
      return { backgroundColor: '#eef2ff', color: '#3547a5' }
    case 'suspended':
      return { backgroundColor: '#fff4e5', color: '#9c5f19' }
    case 'offboarded':
      return { backgroundColor: '#fdecea', color: '#b3261e' }
    default:
      return { backgroundColor: '#eceff1', color: '#455a64' }
  }
}

const getEmployeeSortPriority = (employee) => {
  const status = getEmployeeAccessStatus(employee)
  if (status === 'invited') return 0
  if (employee?.subcontractorRole === 'admin' && status === 'active') return 1
  if (status === 'active') return 2
  if (status === 'suspended') return 3
  return 4
}

const sortByName = (a, b, getName) => getName(a).localeCompare(getName(b))

const getPrimaryPhoneNumber = (item) => {
  if (!item) return ''
  if (typeof item.primaryPhone === 'string' && item.primaryPhone.trim().length) return item.primaryPhone.trim()
  if (typeof item.telephone === 'string' && item.telephone.trim().length) return item.telephone.trim()
  if (Array.isArray(item.phoneNumbers)) {
    const found = item.phoneNumbers.find(number => number?.number)
    if (found?.number) return found.number
  }
  return ''
}

const getVendorContactDisplayName = (contact) => {
  const name = [contact?.firstName, contact?.lastName].filter(Boolean).join(' ').trim()
  return name || contact?.email || 'Unnamed Contact'
}

const getVendorContactOptionLabel = (contact) => {
  const name = getVendorContactDisplayName(contact)
  const email = contact?.email ? ` • ${contact.email}` : ''
  const phone = getPrimaryPhoneNumber(contact) ? ` • ${getPrimaryPhoneNumber(contact)}` : ''
  return `${name}${email}${phone}`
}

const buildFormStateFromContact = (contact, subcontractorRole) => ({
  firstname: contact?.firstName || '',
  lastname: contact?.lastName || '',
  email: contact?.email || '',
  telephone: getPrimaryPhoneNumber(contact),
  subcontractorRole
})

const getCrewTransitionMeta = (crew, vendor) => {
  if (crew?.subcontractorVendor && String(crew.subcontractorVendor) === String(vendor?._id || '')) {
    return {
      mode: 'already-linked',
      title: 'Crew already belongs to this subcontractor',
      detail: `${crew.name || 'This crew'} is already linked to ${vendor?.name || 'this subcontractor'}`
    }
  }

  if (crew?.subcontractorVendor) {
    return {
      mode: 'move-subcontractor',
      title: 'Move crew from another subcontractor',
      detail: `${crew.name || 'This crew'} is already linked to another subcontractor vendor and will be moved`
    }
  }

  return {
    mode: 'convert-internal',
    title: 'Convert internal crew to subcontractor crew',
    detail: `${crew?.name || 'This crew'} does not currently belong to a subcontractor vendor`
  }
}

const resolveCloneNames = (employee) => {
  const firstName = (employee?.firstName || '').trim()
  const lastName = (employee?.lastName || '').trim()

  if (firstName && lastName) {
    return { firstName, lastName }
  }

  const displayName = (employee?.displayName || '').trim()
  if (displayName.includes(',')) {
    const [last, first] = displayName.split(',').map(item => item.trim())
    return {
      firstName: first || firstName || 'Unknown',
      lastName: last || lastName || 'Employee'
    }
  }

  const parts = displayName.split(' ').filter(Boolean)
  if (parts.length >= 2) {
    return {
      firstName: parts[0],
      lastName: parts.slice(1).join(' ')
    }
  }

  return {
    firstName: firstName || displayName || 'Unknown',
    lastName: lastName || 'Employee'
  }
}

const buildUpdatedCrewPayload = (crew, vendorId) => {
  const options = new Set(crew?.crewOptions || [])
  options.add('subContractedHerbicide')
  options.add('sprayContractor')

  return {
    ...crew,
    crewOptions: Array.from(options),
    subcontractorVendor: vendorId
  }
}

const SubcontractorManagementPanel = ({ vendor, onEditVendor, url, user, notification }) => {
  const classes = useStyles()
  const [loading, setLoading] = useState(true)
  const [allEmployees, setAllEmployees] = useState([])
  const [allCrews, setAllCrews] = useState([])
  const [vendorContacts, setVendorContacts] = useState([])
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [showManageCrewsDialog, setShowManageCrewsDialog] = useState(false)
  const [showCreateCrewDialog, setShowCreateCrewDialog] = useState(false)
  const [showCrewReviewDialog, setShowCrewReviewDialog] = useState(false)
  const [inviteTarget, setInviteTarget] = useState(null)
  const [creating, setCreating] = useState(false)
  const [applyingCrewChange, setApplyingCrewChange] = useState(false)
  const [selectedCrew, setSelectedCrew] = useState(null)
  const [selectedVendorContact, setSelectedVendorContact] = useState(null)
  const [selectedExistingCrew, setSelectedExistingCrew] = useState(null)
  const [formState, setFormState] = useState(defaultFormState)
  const [crewReview, setCrewReview] = useState(buildEmptyCrewReview())

  const loadData = useCallback(async () => {
    if (!vendor?._id) return
    setLoading(true)
    try {
      const [employeeResponse, contactResponse] = await Promise.all([
        axios.get(`${url}/api/employees/listEmployees?entity=All&includeInactive=true`),
        axios.get(`${url}/api/vendors/contacts?_id=${vendor._id}`)
      ])

      setAllEmployees(employeeResponse.data?.employees || [])
      setAllCrews(employeeResponse.data?.crews || [])
      setVendorContacts(contactResponse.data || [])
    } catch (error) {
      console.error('Failed to load subcontractor management data', error)
      notification?.({
        type: 'warning',
        title: 'Load Failed',
        message: 'Unable to refresh subcontractor users, contacts, and crews right now.'
      })
    } finally {
      setLoading(false)
    }
  }, [notification, url, vendor?._id])

  useEffect(() => {
    loadData()
  }, [loadData])

  const employees = useMemo(
    () => allEmployees.filter(item => String(item.associatedSub || '') === String(vendor?._id || '')),
    [allEmployees, vendor?._id]
  )

  const crews = useMemo(() => {
    const crewIds = new Set(
      employees
        .map(item => item.currentCrew ? String(item.currentCrew) : null)
        .filter(Boolean)
    )

    return allCrews.filter(crew =>
      String(crew.subcontractorVendor || '') === String(vendor?._id || '') || crewIds.has(String(crew._id))
    )
  }, [allCrews, employees, vendor?._id])

  const summary = useMemo(() => {
    const activeUsers = employees.filter(item => getEmployeeAccessStatus(item) === 'active').length
    const admins = employees.filter(item => item.subcontractorRole === 'admin' && getEmployeeAccessStatus(item) !== 'offboarded').length
    const applicators = employees.filter(item => item.subcontractorRole !== 'admin' && getEmployeeAccessStatus(item) !== 'offboarded').length
    const invited = employees.filter(item => getEmployeeAccessStatus(item) === 'invited').length
    const offboarded = employees.filter(item => getEmployeeAccessStatus(item) === 'offboarded').length

    return {
      activeUsers,
      admins,
      applicators,
      invited,
      offboarded
    }
  }, [employees])

  const crewNameById = useMemo(() => {
    const map = new Map()
    allCrews.forEach(crew => {
      map.set(String(crew._id), crew.name || 'Unnamed Crew')
    })
    return map
  }, [allCrews])

  const crewUsage = useMemo(() => {
    return crews
      .map((crew) => ({
        ...crew,
        memberCount: allEmployees.filter(item => String(item.currentCrew || item.crew || '') === String(crew._id) && item.activeEmployee !== false).length
      }))
      .sort((a, b) => (b.memberCount - a.memberCount) || sortByName(a, b, item => item?.name || ''))
  }, [allEmployees, crews])

  const sortedEmployees = useMemo(() => {
    return [...employees].sort((a, b) => {
      const priorityDifference = getEmployeeSortPriority(a) - getEmployeeSortPriority(b)
      if (priorityDifference !== 0) return priorityDifference
      return sortByName(a, b, getEmployeeDisplayName)
    })
  }, [employees])

  const vendorContactOptions = useMemo(
    () => [...vendorContacts].sort((a, b) => sortByName(a, b, getVendorContactDisplayName)),
    [vendorContacts]
  )

  const crewOptions = useMemo(
    () => [...crews].sort((a, b) => sortByName(a, b, item => item?.name || '')),
    [crews]
  )

  const allCrewOptions = useMemo(
    () => [...allCrews].sort((a, b) => sortByName(a, b, item => item?.name || '')),
    [allCrews]
  )

  const actionItems = useMemo(() => {
    const onboardingStatus = vendor?.onboardingStatus || 'draft'
    const items = []

    if (!vendor?.isSubcontractor) {
      items.push({
        title: 'Enable subcontractor mode',
        detail: 'This vendor must be marked as a subcontractor before invites and crew links become launch-ready.'
      })
    }

    if (vendor?.isSubcontractor && !vendor?.mobileAccessEnabled) {
      items.push({
        title: 'Turn on mobile access',
        detail: 'Subcontractor users are expected to land in the mobile subcontractor experience.'
      })
    }

    if (vendor?.isSubcontractor && onboardingStatus !== 'active') {
      items.push({
        title: 'Move onboarding to active',
        detail: `Current vendor onboarding status is ${onboardingStatus}.`
      })
    }

    if (vendor?.isSubcontractor && summary.admins === 0) {
      items.push({
        title: 'Create an admin user',
        detail: vendorContacts.length
          ? 'You can pick an existing vendor contact first, then send the invite.'
          : 'Admins are the clearest first invite for a new subcontractor org.'
      })
    }

    if (vendor?.isSubcontractor && employees.length === 0) {
      items.push({
        title: 'Create the first subcontractor user',
        detail: vendorContacts.length
          ? 'Start from a vendor contact when possible so the invite details are prefilled.'
          : 'This panel creates the employee record and then hands off to the existing invite flow.'
      })
    } else if (summary.invited > 0) {
      items.push({
        title: 'Follow up on outstanding invites',
        detail: `${summary.invited} invited user${summary.invited === 1 ? '' : 's'} still need to finish setup.`
      })
    }

    if (vendor?.isSubcontractor && crews.length === 0) {
      items.push({
        title: 'Manage crews',
        detail: 'Pull an existing crew in or create a new subcontractor crew without leaving this panel.'
      })
    }

    return items.length ? items : [{
      title: 'Access is in good shape',
      detail: 'This vendor has users, crews, and onboarding settings that look ready for launch.'
    }]
  }, [crews.length, employees.length, summary.admins, summary.invited, vendor, vendorContacts.length])

  const resetCreateDialog = () => {
    setFormState(defaultFormState)
    setSelectedCrew(null)
    setSelectedVendorContact(null)
    setShowCreateDialog(false)
  }

  const openCreateDialog = (subcontractorRole = 'applicator') => {
    setFormState({ ...defaultFormState, subcontractorRole })
    setSelectedCrew(null)
    setSelectedVendorContact(null)
    setShowCreateDialog(true)
  }

  const getCrewMembers = useCallback((crewId) => {
    return allEmployees
      .filter(item => String(item.currentCrew || '') === String(crewId) && item.activeEmployee !== false)
      .sort((a, b) => sortByName(a, b, getEmployeeDisplayName))
  }, [allEmployees])

  const handleVendorContactChange = (event, contact) => {
    setSelectedVendorContact(contact)
    if (!contact) {
      setFormState((current) => ({ ...defaultFormState, subcontractorRole: current.subcontractorRole }))
      return
    }

    setFormState((current) => ({
      ...buildFormStateFromContact(contact, current.subcontractorRole),
      subcontractorRole: current.subcontractorRole
    }))
  }

  const handleCreateUser = async () => {
    const trimmedEmail = formState.email.trim()
    if (!vendor?._id || !formState.firstname || !formState.lastname || !trimmedEmail) {
      notification?.({
        type: 'warning',
        title: 'Missing Details',
        message: 'First name, last name, and email are required before sending a subcontractor invite.'
      })
      return
    }

    setCreating(true)
    try {
      const entityId = vendor.entity || user?.defaultEntity || user?.entities?.[0]?._id || null
      const response = await axios.post(`${url}/api/employees/addNewEmployee`, {
        employee: {
          ...formState,
          email: trimmedEmail,
          entity: entityId,
          associatedSub: vendor._id,
          currentCrew: selectedCrew?._id || null,
          canUseSubcontractorPortal: true,
          subcontractorAccessStatus: 'invited'
        },
        user
      })

      if (response.data?._id) {
        notification?.({
          type: 'success',
          title: 'Subcontractor User Created',
          message: `${response.data.displayName} has been added to ${vendor.name}.`
        })
        setInviteTarget(response.data)
        resetCreateDialog()
        loadData()
      }
    } catch (error) {
      console.error('Failed to create subcontractor employee', error)
      notification?.({
        type: 'warning',
        title: 'Create Failed',
        message: 'Unable to create that subcontractor user.'
      })
    } finally {
      setCreating(false)
    }
  }

  const handleOffboard = async (employee) => {
    const confirmed = window.confirm(`Offboard ${getEmployeeDisplayName(employee)}? This will disable portal access and mark them inactive.`)
    if (!confirmed) return

    try {
      await axios.post(`${url}/api/employees/updateEmployee`, {
        employee: {
          ...employee,
          user: employee.user?._id || employee.user || null,
          associatedSub: vendor._id,
          activeEmployee: false,
          canUseSubcontractorPortal: false,
          subcontractorAccessStatus: 'offboarded',
          offboardedAt: new Date().toISOString()
        }
      })
      notification?.({
        type: 'success',
        title: 'User Offboarded',
        message: `${getEmployeeDisplayName(employee)} no longer has subcontractor access.`
      })
      loadData()
    } catch (error) {
      console.error('Failed to offboard employee', error)
      notification?.({
        type: 'danger',
        title: 'Offboarding Failed',
        message: 'Unable to offboard that subcontractor user.'
      })
    }
  }

  const handleRestoreAccess = async (employee) => {
    try {
      await axios.post(`${url}/api/employees/updateEmployee`, {
        employee: {
          ...employee,
          user: employee.user?._id || employee.user || null,
          associatedSub: vendor._id,
          activeEmployee: true,
          canUseSubcontractorPortal: true,
          subcontractorAccessStatus: employee.user?._id || employee.user ? 'active' : 'invited',
          offboardedAt: null
        }
      })
      notification?.({
        type: 'success',
        title: 'Access Restored',
        message: `${getEmployeeDisplayName(employee)} can access ${vendor.name} again.`
      })
      loadData()
    } catch (error) {
      console.error('Failed to restore subcontractor employee', error)
      notification?.({
        type: 'danger',
        title: 'Restore Failed',
        message: 'Unable to restore that subcontractor user.'
      })
    }
  }

  const openCrewReview = (crew) => {
    if (!crew?._id) return

    const members = getCrewMembers(crew._id).map((employee) => {
      const alreadySubcontractorForVendor = String(employee.associatedSub || '') === String(vendor?._id || '')
      return {
        employeeId: employee._id,
        sourceEmployee: employee,
        createSubcontractorProfile: !alreadySubcontractorForVendor,
        deactivateOriginal: false,
        alreadySubcontractorForVendor
      }
    })

    setCrewReview({
      crew,
      transition: getCrewTransitionMeta(crew, vendor),
      members
    })
    setShowCrewReviewDialog(true)
  }

  const handleCrewReviewToggle = (employeeId, field) => {
    setCrewReview((current) => ({
      ...current,
      members: current.members.map((member) => {
        if (member.employeeId !== employeeId) return member
        if (field === 'createSubcontractorProfile') {
          const nextValue = !member.createSubcontractorProfile
          return {
            ...member,
            createSubcontractorProfile: nextValue,
            deactivateOriginal: nextValue ? member.deactivateOriginal : false
          }
        }
        return {
          ...member,
          [field]: !member[field]
        }
      })
    }))
  }

  const handleApplyCrewChange = async () => {
    if (!crewReview.crew?._id) return

    setApplyingCrewChange(true)
    try {
      await axios.post(`${url}/api/crews/updateCrew`, {
        crew: buildUpdatedCrewPayload(crewReview.crew, vendor._id),
        user
      })

      let createdProfiles = 0
      let updatedOldProfiles = 0
      let deactivatedOldProfiles = 0

      for (const member of crewReview.members) {
        if (!member.createSubcontractorProfile || member.alreadySubcontractorForVendor) {
          continue
        }

        const sourceEmployee = member.sourceEmployee
        const names = resolveCloneNames(sourceEmployee)
        await axios.post(`${url}/api/employees/addNewEmployee`, {
          employee: {
            firstname: names.firstName,
            lastname: names.lastName,
            email: sourceEmployee.user?.email || sourceEmployee.email || '',
            telephone: getPrimaryPhoneNumber(sourceEmployee),
            entity: sourceEmployee.entity,
            associatedSub: vendor._id,
            currentCrew: crewReview.crew._id,
            subcontractorRole: 'applicator',
            subcontractorAccessStatus: 'invited',
            canUseSubcontractorPortal: true
          },
          user
        })
        createdProfiles++

        await axios.post(`${url}/api/employees/updateEmployee`, {
          employee: {
            ...sourceEmployee,
            firstName: names.firstName,
            lastName: names.lastName,
            displayName: `${names.lastName}, ${names.firstName}`,
            user: sourceEmployee.user?._id || sourceEmployee.user || null,
            currentCrew: null,
            activeEmployee: member.deactivateOriginal ? false : sourceEmployee.activeEmployee,
            associatedSub: sourceEmployee.associatedSub || null,
            subcontractorRole: sourceEmployee.subcontractorRole || null,
            subcontractorAccessStatus: sourceEmployee.subcontractorAccessStatus || null,
            canUseSubcontractorPortal: sourceEmployee.canUseSubcontractorPortal || false
          }
        })
        updatedOldProfiles++
        if (member.deactivateOriginal) {
          deactivatedOldProfiles++
        }
      }

      notification?.({
        type: 'success',
        title: 'Crew Updated',
        message: createdProfiles
          ? `${crewReview.crew.name} is now linked to ${vendor.name}. Created ${createdProfiles} subcontractor profile${createdProfiles === 1 ? '' : 's'} and updated ${updatedOldProfiles} original profile${updatedOldProfiles === 1 ? '' : 's'}${deactivatedOldProfiles ? `, deactivating ${deactivatedOldProfiles}` : ''}.`
          : `${crewReview.crew.name} is now linked to ${vendor.name}.`
      })

      setShowCrewReviewDialog(false)
      setShowManageCrewsDialog(false)
      setSelectedExistingCrew(null)
      setCrewReview(buildEmptyCrewReview())
      loadData()
    } catch (error) {
      console.error('Failed to apply crew change', error)
      notification?.({
        type: 'danger',
        title: 'Crew Update Failed',
        message: 'Unable to update that crew or create the subcontractor employee profiles.'
      })
    } finally {
      setApplyingCrewChange(false)
    }
  }

  if (!vendor?._id) {
    return null
  }

  if (loading) {
    return (
      <div className={classes.loadingWrap}>
        <CircularProgress size={28} />
      </div>
    )
  }

  const selectedCrewTransition = selectedExistingCrew ? getCrewTransitionMeta(selectedExistingCrew, vendor) : null
  const selectedCrewMembers = selectedExistingCrew ? getCrewMembers(selectedExistingCrew._id) : []

  return (
    <div className={classes.root}>
      <div className={classes.helperCard}>
        <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
          Subcontractor Access
        </Typography>
        <Typography variant="body2" className={classes.muted}>
          Manage launch readiness for {vendor.name}. User creation here can start from existing vendor contacts, and crew management lets you pull an existing crew into subcontractor scope with a review step.
        </Typography>

        <div className={classes.quickActionGroup}>
          {onEditVendor ? (
            <Button size="small" variant="outlined" color="primary" onClick={onEditVendor}>
              Open Vendor Settings
            </Button>
          ) : null}
          <Button size="small" variant="contained" color="primary" onClick={() => openCreateDialog('admin')} disabled={!vendor.isSubcontractor}>
            Add Admin
          </Button>
          <Button size="small" variant="outlined" color="primary" onClick={() => openCreateDialog('applicator')} disabled={!vendor.isSubcontractor}>
            Add Applicator
          </Button>
          <Button size="small" variant="outlined" color="primary" onClick={() => setShowManageCrewsDialog(true)} disabled={!vendor.isSubcontractor}>
            Manage Crews
          </Button>
        </div>
      </div>

      <div className={classes.summaryRow}>
        <Chip label={`Active ${summary.activeUsers}`} style={{ backgroundColor: '#e5f4ea', color: '#1b5e20' }} />
        <Chip label={`Admins ${summary.admins}`} />
        <Chip label={`Applicators ${summary.applicators}`} />
        <Chip label={`Invited ${summary.invited}`} style={{ backgroundColor: '#eef2ff', color: '#3547a5' }} />
        <Chip label={`Offboarded ${summary.offboarded}`} style={{ backgroundColor: '#fdecea', color: '#b3261e' }} />
        <Chip label={`Crews ${crews.length}`} />
        <Chip label={`Vendor Contacts ${vendorContacts.length}`} />
        <Chip label={vendor.mobileAccessEnabled ? 'Mobile Enabled' : 'Mobile Disabled'} style={vendor.mobileAccessEnabled ? { backgroundColor: '#e5f4ea', color: '#1b5e20' } : { backgroundColor: '#fff4e5', color: '#9c5f19' }} />
      </div>

      <Grid container spacing={2}>
        <Grid item md={7} xs={12}>
          <Paper className={classes.insightCard} elevation={0}>
            <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
              Recommended Next Steps
            </Typography>
            <List dense disablePadding>
              {actionItems.map((item) => (
                <ListItem key={item.title} className={classes.listItem}>
                  <ListItemText primary={item.title} secondary={item.detail} />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
        <Grid item md={5} xs={12}>
          <Paper className={classes.insightCard} elevation={0}>
            <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
              Crew Alignment
            </Typography>
            {crewUsage.length ? (
              <List dense disablePadding>
                {crewUsage.map((crew) => (
                  <ListItem key={crew._id} className={classes.listItem}>
                    <ListItemText
                      primary={crew.name || 'Unnamed Crew'}
                      secondary={`${crew.memberCount} active linked employee${crew.memberCount === 1 ? '' : 's'}`}
                    />
                    <Chip
                      size="small"
                      label={crew.memberCount ? 'Staffed' : 'No users'}
                      className={classes.statusChip}
                      style={crew.memberCount ? { backgroundColor: '#e5f4ea', color: '#1b5e20' } : { backgroundColor: '#eceff1', color: '#455a64' }}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <div className={classes.emptyState}>
                <Typography variant="body2" className={classes.muted}>
                  No subcontractor crews are linked yet. Use Manage Crews to pull in an existing crew or create a new one here.
                </Typography>
              </div>
            )}
          </Paper>
        </Grid>
      </Grid>

      {!vendor.isSubcontractor ? (
        <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
          This vendor is not yet marked as a subcontractor. Update the vendor settings above before inviting subcontractor users or managing crews here.
        </Typography>
      ) : (
        <>
          <div className={classes.actionRow} style={{ marginTop: 24 }}>
            <div>
              <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                Users Linked To {vendor.name}
              </Typography>
              <Typography variant="body2" className={classes.muted}>
                Invite, resend, offboard, and restore access without leaving this panel.
              </Typography>
            </div>
            <Button variant="contained" color="primary" onClick={() => openCreateDialog('applicator')}>
              Add Subcontractor User
            </Button>
          </div>

          <TableContainer className={classes.tableWrap}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Crew</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Portal</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedEmployees.map((employee) => {
                  const accessStatus = getEmployeeAccessStatus(employee)
                  return (
                    <TableRow key={employee._id} hover>
                      <TableCell>{getEmployeeDisplayName(employee)}</TableCell>
                      <TableCell>{employee.subcontractorRole || 'applicator'}</TableCell>
                      <TableCell>{employee.crewName || crewNameById.get(String(employee.currentCrew || '')) || 'Unassigned'}</TableCell>
                      <TableCell>{employee.user?.email || employee.email || '—'}</TableCell>
                      <TableCell>{employee.canUseSubcontractorPortal ? 'Enabled' : 'Disabled'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={accessStatus}
                          style={statusChipStyle(accessStatus)}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" justifyContent="flex-end" gridGap={8}>
                          {accessStatus === 'offboarded' ? (
                            <Button size="small" variant="outlined" color="primary" onClick={() => handleRestoreAccess(employee)}>
                              Restore
                            </Button>
                          ) : (
                            <Button size="small" variant="outlined" onClick={() => setInviteTarget(employee)}>
                              {employee.user?._id ? 'Resend Invite' : 'Send Invite'}
                            </Button>
                          )}
                          {accessStatus !== 'offboarded' ? (
                            <Button size="small" color="secondary" variant="outlined" onClick={() => handleOffboard(employee)}>
                              Offboard
                            </Button>
                          ) : null}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )
                })}
                {sortedEmployees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant="body2" color="textSecondary">
                        No subcontractor users are linked to this vendor yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Dialog open={showCreateDialog} onClose={resetCreateDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{formState.subcontractorRole === 'admin' ? 'Add Subcontractor Admin' : 'Add Subcontractor User'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" className={classes.muted} style={{ marginBottom: 16 }}>
            Pick an existing vendor contact if one exists, or type a new person. Saving here creates the employee record and then opens the existing invite flow.
          </Typography>

          <div className={classes.contactPickerWrap}>
            <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
              Start From An Existing Vendor Contact
            </Typography>
            <Autocomplete
              options={vendorContactOptions}
              getOptionLabel={getVendorContactOptionLabel}
              value={selectedVendorContact}
              onChange={handleVendorContactChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Vendor Contact"
                  variant="outlined"
                  helperText={vendorContactOptions.length ? 'Selecting a contact will prefill the fields below.' : 'No vendor contacts found yet. Type a new user below.'}
                />
              )}
            />
          </div>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="First Name"
                value={formState.firstname}
                onChange={(e) => setFormState({ ...formState, firstname: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                variant="outlined"
                label="Last Name"
                value={formState.lastname}
                onChange={(e) => setFormState({ ...formState, lastname: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                label="Email"
                helperText="Required for the invite flow"
                value={formState.email}
                onChange={(e) => setFormState({ ...formState, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                label="Phone"
                value={formState.telephone}
                onChange={(e) => setFormState({ ...formState, telephone: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                select
                fullWidth
                variant="outlined"
                label="Role"
                value={formState.subcontractorRole}
                onChange={(e) => setFormState({ ...formState, subcontractorRole: e.target.value })}
              >
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="applicator">Applicator</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                options={crewOptions}
                getOptionLabel={(option) => option?.name || 'Unnamed Crew'}
                value={selectedCrew}
                onChange={(event, value) => setSelectedCrew(value)}
                renderInput={(params) => <TextField {...params} label="Assigned Crew" variant="outlined" />}
              />
            </Grid>
            <Grid item xs={12}>
              <Chip size="small" label="Portal access will be enabled automatically" className={classes.statusChip} style={{ backgroundColor: '#edf4ff', color: '#23417e' }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetCreateDialog}>Cancel</Button>
          <Button onClick={handleCreateUser} color="primary" variant="contained" disabled={creating}>
            {creating ? <CircularProgress size={18} /> : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showManageCrewsDialog} onClose={() => setShowManageCrewsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Manage Subcontractor Crews</DialogTitle>
        <DialogContent>
          <Typography variant="body2" className={classes.muted} style={{ marginBottom: 16 }}>
            Pull an existing crew into this subcontractor vendor or create a new one. If the crew already has employees, the next step will let you choose which people should get new subcontractor profiles.
          </Typography>

          <Grid container spacing={2}>
            <Grid item md={6} xs={12}>
              <Paper className={classes.insightCard} elevation={0}>
                <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                  Currently Linked Crews
                </Typography>
                {crewUsage.length ? (
                  <List dense disablePadding>
                    {crewUsage.map((crew) => (
                      <ListItem key={crew._id} className={classes.listItem}>
                        <ListItemText
                          primary={crew.name || 'Unnamed Crew'}
                          secondary={`${crew.memberCount} active employee${crew.memberCount === 1 ? '' : 's'}`}
                        />
                        <Button size="small" variant="outlined" color="primary" onClick={() => openCrewReview(crew)}>
                          Review
                        </Button>
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <div className={classes.emptyState}>
                    <Typography variant="body2" className={classes.muted}>
                      No crews are linked to this vendor yet.
                    </Typography>
                  </div>
                )}
              </Paper>
            </Grid>
            <Grid item md={6} xs={12}>
              <Paper className={classes.insightCard} elevation={0}>
                <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 12 }}>
                  Attach An Existing Crew
                </Typography>
                <Autocomplete
                  options={allCrewOptions}
                  getOptionLabel={(option) => option?.name || 'Unnamed Crew'}
                  value={selectedExistingCrew}
                  onChange={(event, value) => setSelectedExistingCrew(value)}
                  renderInput={(params) => <TextField {...params} label="Existing Crew" variant="outlined" />}
                />

                {selectedExistingCrew ? (
                  <div style={{ marginTop: 16 }}>
                    <div className={classes.summaryRow}>
                      <Chip label={selectedCrewTransition?.title || 'Crew selected'} />
                      <Chip label={`${selectedCrewMembers.length} active crew member${selectedCrewMembers.length === 1 ? '' : 's'}`} />
                      <Chip label={selectedExistingCrew.subcontractorVendor ? 'Already subcontractor-linked' : 'Currently internal'} style={selectedExistingCrew.subcontractorVendor ? { backgroundColor: '#eef2ff', color: '#3547a5' } : { backgroundColor: '#fff4e5', color: '#9c5f19' }} />
                    </div>
                    <Typography variant="body2" className={classes.muted}>
                      {selectedCrewTransition?.detail}
                    </Typography>
                    <Button style={{ marginTop: 16 }} variant="contained" color="primary" onClick={() => openCrewReview(selectedExistingCrew)}>
                      Review Crew Assignment
                    </Button>
                  </div>
                ) : (
                  <FormHelperText>Select any active crew to review how it should move into subcontractor scope.</FormHelperText>
                )}

                <Divider style={{ margin: '20px 0 16px' }} />

                <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                  Need A Brand-New Crew?
                </Typography>
                <Typography variant="body2" className={classes.muted} style={{ marginTop: 8, marginBottom: 16 }}>
                  You can still create a new crew here. It will be pre-linked to this subcontractor vendor and configured for subcontracted herbicide mobile use.
                </Typography>
                <Button variant="outlined" color="primary" onClick={() => setShowCreateCrewDialog(true)}>
                  Create New Crew
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowManageCrewsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showCrewReviewDialog} onClose={() => setShowCrewReviewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{crewReview.transition?.title || 'Review Crew Change'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {crewReview.transition?.detail}. You are linking this crew to {vendor.name}. For any current crew member you select below, I will create a new subcontractor employee profile tied to this vendor and crew. If selected, the original profile will be removed from this crew and can also be deactivated. Anyone left unchecked will stay on the crew exactly as they are today.
          </DialogContentText>

          <div className={classes.summaryRow}>
            <Chip label={crewReview.crew?.name || 'No crew selected'} />
            <Chip label={`${crewReview.members.length} active crew member${crewReview.members.length === 1 ? '' : 's'}`} />
            <Chip label={`Target vendor ${vendor.name}`} />
          </div>

          {crewReview.members.length ? (
            <>
              {crewReview.members.map((member) => {
                const employee = member.sourceEmployee
                const accessStatus = getEmployeeAccessStatus(employee)
                return (
                  <div key={member.employeeId} className={classes.reviewRow}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item md={5} xs={12}>
                        <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
                          {getEmployeeDisplayName(employee)}
                        </Typography>
                        <Typography variant="body2" className={classes.muted}>
                          {employee.user?.email || employee.email || 'No email on file'}
                        </Typography>
                        <Typography variant="body2" className={classes.muted}>
                          {getPrimaryPhoneNumber(employee) || 'No phone on file'}
                        </Typography>
                      </Grid>
                      <Grid item md={3} xs={12}>
                        <div className={classes.summaryRow} style={{ marginBottom: 0 }}>
                          <Chip size="small" label={employee.associatedSub ? 'Already tied to a subcontractor vendor' : 'Internal employee profile'} style={employee.associatedSub ? { backgroundColor: '#eef2ff', color: '#3547a5' } : { backgroundColor: '#fff4e5', color: '#9c5f19' }} />
                          <Chip size="small" label={accessStatus} style={statusChipStyle(accessStatus)} />
                        </div>
                      </Grid>
                      <Grid item md={4} xs={12}>
                        {member.alreadySubcontractorForVendor ? (
                          <Typography variant="body2" className={classes.muted}>
                            This person already has a subcontractor profile under {vendor.name}.
                          </Typography>
                        ) : (
                          <>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={member.createSubcontractorProfile}
                                  onChange={() => handleCrewReviewToggle(member.employeeId, 'createSubcontractorProfile')}
                                  color="primary"
                                />
                              }
                              label="Create subcontractor profile"
                            />
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={member.deactivateOriginal}
                                  onChange={() => handleCrewReviewToggle(member.employeeId, 'deactivateOriginal')}
                                  color="primary"
                                  disabled={!member.createSubcontractorProfile}
                                />
                              }
                              label="Deactivate old profile"
                            />
                          </>
                        )}
                      </Grid>
                    </Grid>
                  </div>
                )
              })}
              <FormHelperText>
                Employees left unchecked will stay on this crew with their current profile. Selected employees will get a new subcontractor profile, and their old profile will be removed from this crew and optionally deactivated.
              </FormHelperText>
            </>
          ) : (
            <div className={classes.emptyState}>
              <Typography variant="body2" className={classes.muted}>
                No active employees are currently attached to this crew. Applying the change will just link the crew to {vendor.name}.
              </Typography>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCrewReviewDialog(false)}>Cancel</Button>
          <Button onClick={handleApplyCrewChange} color="primary" variant="contained" disabled={applyingCrewChange}>
            {applyingCrewChange ? <CircularProgress size={18} /> : 'Apply Crew Change'}
          </Button>
        </DialogActions>
      </Dialog>

      <AddCrewModal
        open={showCreateCrewDialog}
        closeModal={() => setShowCreateCrewDialog(false)}
        updateParent={() => {
          setShowCreateCrewDialog(false)
          loadData()
        }}
        defaultSubcontractorVendor={vendor}
      />

      <InviteUserModal
        open={!!inviteTarget}
        employee={inviteTarget || {}}
        inviteVariant="subcontractor"
        updateParent={() => {
          setInviteTarget(null)
          loadData()
        }}
      />
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user,
  notification: state.notification
})

export default connect(mapStateToProps)(SubcontractorManagementPanel)
